import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, resolveImageSrc } from "@/lib/api";
import { S } from "./ibloom/styles";
import { slugify, productAlt } from "@/lib/seo";

/**
 * Public category landing page at /category/{slug}. SEO infrastructure page —
 * lists every product whose category matches the slug, each linking to its
 * own product page. No auth required.
 */
export default function CategoryPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api.get("/mapbazzar/products")
      .then((r) => active && setProducts(r.data || []))
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  const matches = useMemo(
    () => products.filter((p) => slugify(p.category) === slug),
    [products, slug]
  );
  const label = (slug || "Local").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div style={{ maxWidth: 640, margin: "0 auto", minHeight: "100dvh", background: "#FAFAF5" }} data-testid="category-page">
      <div style={{ background: "linear-gradient(135deg, #FFF9F0, #FDE7C8)", padding: "28px 22px", borderBottom: "1px solid #F3CC8B" }}>
        <h1 style={{ ...S.fraunces, fontSize: 24, fontWeight: 700, margin: 0, color: "#1A1A1A" }} data-testid="category-page-title">
          {label} Shops in Ahmedabad
        </h1>
        <div style={{ fontSize: 12.5, color: "#5C3D12", marginTop: 6 }}>
          Local {label.toLowerCase()} listings on Mapbazar · same-day delivery
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {loading ? (
          <div style={{ padding: 24, textAlign: "center", color: "#888", fontSize: 13 }}>Loading…</div>
        ) : matches.length === 0 ? (
          <div data-testid="category-page-empty" style={{ padding: 24, textAlign: "center", background: "white", border: "1px dashed #D5D3C7", borderRadius: 14, color: "#888", fontSize: 13 }}>
            No listings in this category yet.
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
            {matches.map((p) => (
              <a key={p.id} href={p.slug ? `/product/${p.slug}` : "#"} data-testid={`category-product-${p.id}`}
                style={{ background: "white", border: "1px solid #EAE8DC", borderRadius: 14, overflow: "hidden", textDecoration: "none", color: "inherit" }}>
                <div style={{ width: "100%", aspectRatio: "1/1", background: "#F6FAF7" }}>
                  {p.image && <img src={resolveImageSrc(p.image)} alt={productAlt(p.name, p.shop_name, p.shop_address)} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                </div>
                <div style={{ padding: "8px 10px 10px" }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1A1A1A", lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.name}>{p.name}</div>
                  <div style={{ ...S.fraunces, fontSize: 15, fontWeight: 800, color: "#0F3D2E", marginTop: 4 }}>₹{p.price}</div>
                </div>
              </a>
            ))}
          </div>
        )}
        <div style={{ textAlign: "center", padding: 22 }}>
          <button onClick={() => navigate("/")} style={{ background: "none", border: "none", color: "#0F3D2E", fontWeight: 700, cursor: "pointer", fontSize: 12 }}>
            → Explore the full Mapbazar map
          </button>
        </div>
      </div>
    </div>
  );
}
